'use client';

import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { CommitmentTypeEnum } from '@sola/schema/dotcom/cart/commitments.js';
import { PaymentFrequencyType } from '@sola/schema/dotcom/proxy-api/transactions.js';
import {
  GraphQLCommitment,
  GraphQLGivingTotals,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks.js';
import {
  commitmentsQuery,
  givingTotalsQuery,
} from '@sola/supporter-sdk/queries.js';

import { CommitmentCard } from '../../molecules/commitment-card/commitment-card.js';
import { CTA_CONTACT_SUPPORT } from '../../utils/ctas.js';
import { SectionCardList } from '../section-card-list/section-card-list.js';
import {
  CommitmentCardData,
  getCommitmentTotal,
  getSortCommitmentsByTitle,
  groupCommitmentsByPaymentFrequency,
  groupCommitmentsByType,
  groupHasCommitments,
} from './utils.js';

/**
 * Get component data
 */
export const useComponentData = () => {
  const { t } = useTranslation('my');
  const { data, isLoading, isFetching, isPending, isError } = useSupporterQuery(
    [givingTotalsQuery, commitmentsQuery],
  );

  let commitments: GraphQLCommitment[] = [];
  let commitmentFrequencyGroups: CommitmentCardData[] = [];

  // @ts-expect-error //TODO This should be fixed once we tighten up useSupporterQuery types.
  const [givingTotals, commitmentsQueryData]: [
    GraphQLGivingTotals | undefined,
    GraphQLCommitment[] | undefined,
  ] = data;

  if (commitmentsQueryData?.length) {
    commitments = commitmentsQueryData.filter(
      (commitment: GraphQLCommitment) => {
        return (
          commitment.isRecurring === true && commitment.status === 'Active'
        );
      },
    );
  }

  if (commitments?.length) {
    const {
      [PaymentFrequencyType.Monthly]: monthlyCommitments,
      [PaymentFrequencyType.Quarterly]: quarterlyCommitments,
      [PaymentFrequencyType.Semiannually]: semiannualCommitments,
      [PaymentFrequencyType.Annually]: annualCommitments,
    } = groupCommitmentsByPaymentFrequency(commitments);

    commitmentFrequencyGroups = [
      {
        heading: t('commitments.frequencies.monthly'),
        frequencyAbbr: t('commitments.frequencyAbbr.monthly'),
        commitments: monthlyCommitments,
      },
      {
        heading: t('commitments.frequencies.quarterly'),
        frequencyAbbr: t('commitments.frequencyAbbr.quarterly'),
        commitments: quarterlyCommitments,
      },
      {
        heading: t('commitments.frequencies.semiannual'),
        frequencyAbbr: t('commitments.frequencyAbbr.semiAnnual'),
        commitments: semiannualCommitments,
      },
      {
        heading: t('commitments.frequencies.annual'),
        frequencyAbbr: t('commitments.frequencyAbbr.annual'),
        commitments: annualCommitments,
      },
    ];
  }

  return {
    isShowingSkeletons: (isLoading || isFetching || isPending) && !isError,
    commitmentFrequencyGroups,
    givingTotals,
    sortCommitmentsByTitle: getSortCommitmentsByTitle(t),
    hasError: isError,
  };
};

/**
 * Displays list of commitments based on frequency.
 * @TODO - need to talk to UX about UI when there are no commitments.
 */
export const CommitmentLists = () => {
  const {
    isShowingSkeletons,
    commitmentFrequencyGroups,
    hasError,
    sortCommitmentsByTitle,
  } = useComponentData();
  const { t } = useTranslation(['my', 'common']);

  return (
    <div className="max-w-md">
      {isShowingSkeletons && (
        <SectionCardList isLoadingCards isLoadingHeading />
      )}

      {!isShowingSkeletons &&
        !!commitmentFrequencyGroups.length &&
        commitmentFrequencyGroups.map(
          (
            { heading, frequencyAbbr, commitments: commitmentsByFrequency },
            index,
          ) => {
            const commitmentsByType = groupCommitmentsByType(
              commitmentsByFrequency,
            );

            /** NOTE: Pull childSponsorships and giftCommitments out so that we can list before other commitment types */
            const {
              [CommitmentTypeEnum.ChildSponsorship]: childSponsorships,
              [CommitmentTypeEnum.GiftCommitment]: giftCommitments,
              ...remainingCommitmentTypes
            } = commitmentsByType;
            const remainingCommitments = Object.keys(remainingCommitmentTypes)
              .map((commitmentType) => remainingCommitmentTypes[commitmentType])
              .flat();

            return (
              groupHasCommitments(commitmentsByType) && (
                <div
                  key={`commitment-group-${heading}`}
                  className="py-comfy-xl"
                  style={{
                    borderTop:
                      index > 0
                        ? '1px solid var(--color-container-border-neutral-mild)'
                        : undefined,
                  }}
                >
                  <SectionCardList
                    heading={heading}
                    subheading={
                      <>
                        {t('common:numbers.currency', {
                          amount: getCommitmentTotal(commitmentsByFrequency),
                        })}
                        <span className="label-neutral-mild">
                          {frequencyAbbr}
                        </span>
                      </>
                    }
                  >
                    {[
                      ...childSponsorships.toSorted(sortCommitmentsByTitle),
                      ...giftCommitments.toSorted(sortCommitmentsByTitle),
                      ...remainingCommitments.toSorted(sortCommitmentsByTitle),
                    ].map((commitment) => {
                      return (
                        <CommitmentCard
                          key={`commitment-card-${commitment.id}`}
                          commitment={commitment}
                        />
                      );
                    })}
                  </SectionCardList>
                </div>
              )
            );
          },
        )}

      {!isShowingSkeletons &&
        !hasError &&
        !commitmentFrequencyGroups.length && (
          <>
            <h3 className="heading-sm mb-snug-xl">
              {t('my:commitments.noCommitments.title')}
            </h3>

            <p className="body-sm label-neutral-mild">
              {t('my:commitments.noCommitments.message')}
            </p>

            <p className="flex">
              <ButtonStyle className="" size="md" variant="primary">
                <a href="/sponsor-a-child">
                  {t('commitments.noCommitments.ctaText')}
                </a>
              </ButtonStyle>
            </p>
          </>
        )}

      {!isShowingSkeletons && hasError && (
        <Alert
          variant="negative"
          title={t('commitments.queryError.title')}
          message={t('commitments.queryError.message')}
          action={
            <a href={CTA_CONTACT_SUPPORT.url}>
              {t(CTA_CONTACT_SUPPORT.translation)}
            </a>
          }
        />
      )}
    </div>
  );
};
